/** @jsxImportSource theme-ui */
import React from 'react';
import { useSelector } from 'react-redux';
import { SxProp } from 'theme-ui';

import { RiskMagnitude } from 'models/RiskCategories';
import { pluralize } from 'utils/stringFormattingHelpers';

import { Typography } from 'components/_common/Typography';
import BdPrimaryLink from 'components/_controls/BdLink/BdPrimaryLink';

import diseasesSelectors from 'diseases/diseases.selectors';
import riskOverviewSelectors from 'riskOverview/riskOverview.selectors';

type RiskOverviewImportationDescriptionProps = SxProp & {
  activityId: number;
  locationId: number;
  diseaseId: number;
  riskMagnitude: RiskMagnitude;
};

const RiskOverviewImportationDescription: React.FC<RiskOverviewImportationDescriptionProps> = ({
  activityId,
  locationId,
  diseaseId,
  riskMagnitude,
  ...props
}) => {
  const importationSources = useSelector(
    riskOverviewSelectors.getImportationSourcesByActivityIdAndLocationId(activityId, locationId)
  );
  const diseaseName = useSelector(diseasesSelectors.getDiseaseName(diseaseId));

  const locationsList = (importationSources || []).slice(0, 3).map(source => (
    <BdPrimaryLink
      data-testid={`importation-link-${source.geonameId}`}
      key={`import-${activityId}-${locationId}-${source.geonameId}`}
      href={`/location/${locationId}/disease/${diseaseId}/species/1/country/${source.geonameId}/parameters`}
      inline
      sx={{ pb: '0', textDecoration: 'underline' }}
    >
      {source.fullDisplayName}
    </BdPrimaryLink>
  ));

  const remainingCountries = importationSources?.length - 3;

  const moreCountriesText =
    remainingCountries > 0 ? (
      <React.Fragment key={`more-locations-${activityId}-${locationId}`}>
        &nbsp;and&nbsp;
        <BdPrimaryLink
          data-testid="on-click-more-locations"
          href={`/location/${locationId}/disease/${diseaseId}/species/1?activity=true`}
          inline
          sx={{ pb: '0', textDecoration: 'underline' }}
        >
          {remainingCountries}
          {pluralize(remainingCountries, ' more country', ' more countries')}
        </BdPrimaryLink>
      </React.Fragment>
    ) : (
      []
    );

  return (
    <Typography
      data-testid="ro-importation-risk-details"
      variant="body2"
      color="stone70"
      {...props}
    >
      {riskMagnitude === 'Not calculated' ? (
        'Risk of importation is not currently calculated.'
      ) : riskMagnitude === 'Calculating, revisit later!' ? (
        'Risk of importation is being calculated. Please check back later.'
      ) : !importationSources?.length ? (
        'There are no sources of importation to your location.'
      ) : (
        <>
          Cases of {diseaseName} are most likely to be imported from&nbsp;
          {[...locationsList, moreCountriesText].reduce(
            (prev, curr) => curr != null && [prev, ', ', curr]
          )}
          .
        </>
      )}
    </Typography>
  );
};

export default RiskOverviewImportationDescription;
